import { useEffect, useState, useMemo } from "react";
import problemServices from "../services/problem-service";

const useFetchProblems = (
  page,
  pageSize,
  sortBy,
  sortOrder,
  tags = [],
  filters = [],
  searchTerm = "",
  skip = false,
  isUserContext = false
) => {
  const [problems, setProblems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stabilize tags and filters
  const stableTags = useMemo(() => tags || [], [tags]);
  const stableFilters = useMemo(() => filters || [], [filters]);

  useEffect(() => {
    const shouldSkip =
      skip &&
      stableTags.length === 0 &&
      stableFilters.length === 0 &&
      !searchTerm;

    if (shouldSkip) {
      setProblems([]);
      setTotalCount(0);
      setIsLoading(false);
      setError(null);
      return;
    }
    const fetchProblems = async () => {
      setIsLoading(true);
      try {
        const criteria = stableFilters.map(
          ({ key, operator, searchTerm }) => `${key}.${operator}=${searchTerm}`
        );

        const res = await problemServices.getProblemsForNavigation(
          page - 1,
          pageSize,
          sortBy,
          sortOrder,
          [...stableTags, ...criteria],
          searchTerm
        );

        if (res.statusText !== "OK") {
          throw new Error(`Error ${res.status}: ${res.statusText}`);
        }

        let formattedProblems = res.data;
        if (isUserContext) {
          formattedProblems = res.data.map((problem) => ({
            id: problem.id,
            title: problem.defaultTitle || "Untitled",
            tags:
              problem.problemTags?.map((tag) => tag.name).join(", ") || "N/A",
            name: problem.name || "unknown",
            forPaidOnly: problem.forPaidOnly,
            hasVideos: problem.hasVideos,
          }));
        }

        setProblems(formattedProblems);
        setTotalCount(Number(res.headers["x-total-count"] || 0));
        setError(null);
      } catch (error) {
        setProblems([]);
        setTotalCount(0);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProblems();
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    stableTags,
    stableFilters,
    searchTerm,
    skip,
    isUserContext,
  ]);

  return { problems, totalCount, isLoading, error };
};

export default useFetchProblems;
